import { useEffect, useState } from 'react'
import { useMatch } from 'react-router-dom'
import { WhatsAppBrandIcon } from '@/components/icons/SocialBrandIcons'
import { cn } from '@/lib/utils'

const FOOTER_GAP_PX = 10

interface WhatsAppButtonProps {
  phone?: string
  aboveBottomNav?: boolean
  /** Extra px above the bottom-nav offset (e.g. cart checkout bar). */
  extraBottomOffsetPx?: number
}

export function WhatsAppButton({
  phone,
  aboveBottomNav = false,
  extraBottomOffsetPx = 0,
}: WhatsAppButtonProps) {
  const isProductDetail = Boolean(useMatch('/shop/:id'))
  const [liftPx, setLiftPx] = useState(0)

  useEffect(() => {
    const updatePosition = () => {
      const doc = document.documentElement
      const distanceFromBottom =
        doc.scrollHeight - doc.scrollTop - window.innerHeight
      const atPageBottom = distanceFromBottom <= 2

      if (!atPageBottom) {
        setLiftPx(0)
        return
      }

      const footerCredit = document.getElementById('store-footer-credit')
      setLiftPx(
        footerCredit
          ? footerCredit.offsetHeight + FOOTER_GAP_PX
          : FOOTER_GAP_PX
      )
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [])

  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  const url = `https://wa.me/${digits}?text=${encodeURIComponent('Hello Ooudh Kraft, I need help with an order.')}`

  const useBottomNavOffset = aboveBottomNav && !isProductDetail
  const bottomStyle =
    useBottomNavOffset && extraBottomOffsetPx > 0
      ? {
          bottom: `calc(var(--store-bottom-nav-height) + var(--store-bottom-nav-gap) + env(safe-area-inset-bottom, 0px) + 0.75rem + ${extraBottomOffsetPx}px)`,
        }
      : undefined

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={{
        ...bottomStyle,
        transform:
          liftPx > 0
            ? `translateY(-${liftPx}px) scale(1)`
            : undefined,
      }}
      className={cn(
        'fixed right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/40 transition-[transform] duration-300 hover:scale-105',
        isProductDetail
          ? 'product-mobile-bar-offset-bottom lg:bottom-6'
          : useBottomNavOffset && extraBottomOffsetPx <= 0
            ? 'store-bottom-nav-offset lg:bottom-6'
            : useBottomNavOffset && extraBottomOffsetPx > 0
              ? 'lg:bottom-6'
              : 'bottom-6'
      )}
      aria-label="WhatsApp support"
    >
      <WhatsAppBrandIcon className="h-7 w-7" />
    </a>
  )
}
